import { request } from "./http";

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function toMoney(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

function unwrap(payload) {
  const raw = asObject(payload);
  if (raw.item !== undefined) return raw.item;
  if (raw.items !== undefined) return raw.items;
  return payload;
}

function normalizePage(payload, fallbackPage = 0, fallbackSize = 20) {
  const raw = unwrap(payload);
  if (Array.isArray(raw)) {
    return {
      items: raw,
      page: fallbackPage,
      size: fallbackSize,
      totalElements: raw.length,
      totalPages: raw.length ? 1 : 0,
    };
  }

  const page = asObject(raw);
  const content = asArray(page.content || page.items);
  return {
    items: content,
    page: Number.isFinite(Number(page.number)) ? Number(page.number) : fallbackPage,
    size: Number.isFinite(Number(page.size)) ? Number(page.size) : fallbackSize,
    totalElements: Number.isFinite(Number(page.totalElements)) ? Number(page.totalElements) : content.length,
    totalPages: Number.isFinite(Number(page.totalPages)) ? Number(page.totalPages) : (content.length ? 1 : 0),
  };
}

function normalizeWallet(item = {}) {
  const raw = asObject(item);
  const available = toMoney(raw.balance ?? raw.availableBalance ?? raw.availableAmount ?? raw.available);
  const pending = toMoney(raw.pendingAmount ?? raw.frozenAmount ?? raw.onHold) ?? 0;
  const total = toMoney(raw.netEarned ?? raw.totalEarned ?? raw.total);
  const derivedAvailable = available != null ? available : total != null ? Math.max(0, total - pending) : 0;

  return {
    // Доступный баланс: только реально начисленные средства, готовые к выводу.
    // Отменённые/возвращённые записи должны уходить с backend как REFUND и не попадать в balance.
    balance: derivedAvailable,
    availableBalance: derivedAvailable,
    netEarned: toMoney(raw.netEarned) ?? total ?? derivedAvailable,
    totalEarned: toMoney(raw.totalEarned) ?? total ?? derivedAvailable,
    pendingAmount: pending,
    totalWithdrawn: Number(raw.totalWithdrawn ?? 0) || 0,
    currency: String(raw.currency || "RUB"),
    lastOperationAt: raw.lastOperationAt || null,
  };
}

function normalizePayout(item = {}) {
  const raw = asObject(item);
  return {
    id: raw.id ?? null,
    status: String(raw.status || "").toUpperCase(),
    amount: Number(raw.amount || 0) || 0,
    currency: String(raw.currency || "RUB"),
    comment: String(raw.comment || ""),
    recipientName: String(raw.recipientName || ""),
    bankName: String(raw.bankName || ""),
    bankAccount: String(raw.bankAccount || ""),
    bankBik: String(raw.bankBik || ""),
    walletNumber: String(raw.walletNumber || ""),
    adminComment: String(raw.adminComment || ""),
    requestedAt: raw.requestedAt || null,
    approvedAt: raw.approvedAt || null,
    processedAt: raw.processedAt || null,
    payoutReference: String(raw.payoutReference || ""),
    updatedAt: raw.updatedAt || null,
  };
}

export const walletApi = {
  async getMyWallet() {
    const payload = await request("/api/me/wallet");
    return normalizeWallet(unwrap(payload));
  },

  async listPayouts({ page = 0, size = 20 } = {}) {
    const payload = await request(`/api/me/wallet/payouts?page=${Number(page) || 0}&size=${Number(size) || 20}`);
    const normalized = normalizePage(payload, Number(page) || 0, Number(size) || 20);
    return {
      ...normalized,
      items: normalized.items.map(normalizePayout),
    };
  },

  async createPayout(payload = {}) {
    const created = await request("/api/me/wallet/payouts", {
      method: "POST",
      json: payload,
    });
    return normalizePayout(unwrap(created));
  },

  async cancelPayout(payoutId) {
    if (!payoutId) throw new Error("payoutId is required");
    const cancelled = await request(`/api/me/wallet/payouts/${encodeURIComponent(String(payoutId))}/cancel`, {
      method: "POST",
    });
    return normalizePayout(unwrap(cancelled));
  },
};
